/**
 * 合规开户流程控制工具
 * 当 complianceAuditFlag 为 'Y' 时，按照指定顺序进行开户流程
 */

import {
    isComplianceAuditFlow,
    setComplianceAuditFlag,
    clearComplianceAuditFlag,
    detectAndSetComplianceFlags,
    getAllComplianceFlags
} from './complianceSession'

// 合规开户流程步骤定义
export const COMPLIANCE_FLOW_STEPS = [
    {
        name: 'PASSWORD_VERIFY',
        path: '/compliancePasswordVerify',
        title: '交易密码验证'
    },
    {
        name: 'EMPLOYMENT',
        path: '/jobStatus',
        title: '职业状况'
    },
    {
        name: 'FINANCE',
        path: '/Finance',
        title: '财务状态'
    },
    {
        name: 'INFORMATION_SUPPLEMENT',
        path: '/informationSupplement',
        title: '补充信息'
    },
    {
        name: 'BACKGROUND_DISCLOSURE',
        path: '/backgroundDisclosure',
        title: '背景状况披露'
    },
    {
        name: 'SIGNATURE',
        path: '/signature',
        title: '客户签名'
    },
    {
        name: 'COMPLIANCE_AUDIT',
        path: '/complianceAudit',
        title: '合规审核'
    }
]

/**
 * 检查是否为合规开户流程
 * @returns {boolean}
 */
export function isComplianceFlow() {
    return isComplianceAuditFlow()
}

/**
 * 设置合规开户标识
 * @param {string} flag - 'Y' 或 'true' 表示合规流程
 */
export function setComplianceFlag(flag) {
    setComplianceAuditFlag(flag)
}

/**
 * 获取当前步骤在合规流程中的索引
 * @param {string} currentStepName - 当前步骤名称
 * @returns {number} 步骤索引，-1 表示不在流程中
 */
export function getCurrentStepIndex(currentStepName) {
    return COMPLIANCE_FLOW_STEPS.findIndex(step => step.name === currentStepName)
}

/**
 * 获取下一步骤信息
 * @param {string} currentStepName - 当前步骤名称
 * @returns {object|null} 下一步骤信息，null 表示已是最后一步
 */
export function getNextStep(currentStepName) {
    const currentIndex = getCurrentStepIndex(currentStepName)
    if (currentIndex === -1 || currentIndex === COMPLIANCE_FLOW_STEPS.length - 1) {
        return null
    }
    return COMPLIANCE_FLOW_STEPS[currentIndex + 1]
}

/**
 * 获取上一步骤信息
 * @param {string} currentStepName - 当前步骤名称
 * @returns {object|null} 上一步骤信息，null 表示已是第一步
 */
export function getPreviousStep(currentStepName) {
    const currentIndex = getCurrentStepIndex(currentStepName)
    if (currentIndex <= 0) {
        return null
    }
    return COMPLIANCE_FLOW_STEPS[currentIndex - 1]
}

/**
 * 合规流程跳转到下一步
 * @param {object} router - Vue Router 实例
 * @param {string} currentStepName - 当前步骤名称
 * @param {object} options - 跳转选项
 * @returns {boolean} 是否成功跳转
 */
export function goToNextComplianceStep(router, currentStepName, options = {}) {
    if (!isComplianceFlow()) {
        return false
    }
    
    const nextStep = getNextStep(currentStepName)
    if (!nextStep) {
        console.warn('已是合规流程的最后一步')
        return false
    }
    
    console.log(`合规流程：从 ${currentStepName} 跳转到 ${nextStep.name}`)
    
    // 跳转到下一步
    router.push({
        path: nextStep.path,
        query: {
            ...options.query,
            compliance: 'Y'
        }
    })
    
    return true
}

/**
 * 合规流程跳转到上一步
 * @param {object} router - Vue Router 实例
 * @param {string} currentStepName - 当前步骤名称
 * @param {object} options - 跳转选项
 * @returns {boolean} 是否成功跳转
 */
export function goToPreviousComplianceStep(router, currentStepName, options = {}) {
    if (!isComplianceFlow()) {
        return false
    }
    
    const previousStep = getPreviousStep(currentStepName)
    if (!previousStep) {
        console.warn('已是合规流程的第一步')
        return false
    }
    
    console.log(`合规流程：从 ${currentStepName} 返回到 ${previousStep.name}`)
    
    // 跳转到上一步
    router.push({
        path: previousStep.path,
        query: {
            ...options.query,
            compliance: 'Y'
        }
    })
    
    return true
}

/**
 * 检查是否需要进入合规流程
 * @param {object} route - 当前路由对象
 * @returns {boolean}
 */
export function shouldEnterComplianceFlow(route) {
    // 使用统一的检测函数
    const result = detectAndSetComplianceFlags({ route })

    // 如果检测到合规标识或者已经存在合规标识，则进入合规流程
    return result.complianceDetected || isComplianceAuditFlow()
}

/**
 * 初始化合规流程
 * @param {object} route - 当前路由对象
 */
export function initComplianceFlow(route) {
    if (shouldEnterComplianceFlow(route)) {
        setComplianceFlag('Y')
        console.log('合规开户流程已启动')
    }
}

/**
 * 获取合规流程进度信息
 * @param {string} currentStepName - 当前步骤名称
 * @returns {object} 进度信息
 */
export function getComplianceProgress(currentStepName) {
    const currentIndex = getCurrentStepIndex(currentStepName)
    const totalSteps = COMPLIANCE_FLOW_STEPS.length
    
    return {
        currentStep: currentIndex + 1,
        totalSteps,
        progress: Math.round(((currentIndex + 1) / totalSteps) * 100),
        stepName: currentStepName,
        stepTitle: COMPLIANCE_FLOW_STEPS[currentIndex]?.title || ''
    }
}

/**
 * 清除合规流程标识
 */
export function clearComplianceFlag() {
    clearComplianceAuditFlag()
}

/**
 * 启动合规流程（从密码验证开始）
 * @param {object} router - Vue Router 实例
 * @param {object} options - 跳转选项
 */
export function startComplianceFlow(router, options = {}) {
    // 设置合规标识
    setComplianceFlag('Y')

    // 跳转到密码验证页面（合规流程第一步）
    const firstStep = COMPLIANCE_FLOW_STEPS[0]
    console.log('启动合规流程，跳转到密码验证页面')

    router.push({
        path: firstStep.path,
        query: {
            ...options.query,
            compliance: 'Y'
        }
    })
}

/**
 * 验证合规流程步骤顺序
 * @param {string} targetStepName - 目标步骤名称
 * @param {string} currentStepName - 当前步骤名称
 * @returns {boolean} 是否允许跳转
 */
export function validateComplianceStepOrder(targetStepName, currentStepName) {
    if (!isComplianceFlow()) {
        return true
    }
    
    const targetIndex = getCurrentStepIndex(targetStepName)
    const currentIndex = getCurrentStepIndex(currentStepName)
    
    // 允许向前跳转一步，或者向后跳转
    return targetIndex <= currentIndex + 1
}
